// useGetTasks(workspaceId: number) hook

import { useMutation, useQuery, QueryKey } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';
import Task, { TaskStage } from '../types/Task';
import {
  addCommentReaction,
  createPlaceholderTask,
  getTaskActivity,
  getTaskAttachments,
  getTaskByNumber,
  getTaskComments,
  getTaskLinks,
  getTasksForGivenSpace,
  getTaskSubtasks,
  getTaskDependencies,
  addTaskDependency,
  deleteTaskDependency,
  removeCommentReaction,
  updateTask,
  updateTaskLabels,
} from '../api/task.api';
import { CommentReaction } from '../types/Comment';
import { TaskActivityResponse } from '../types/TaskActivity';
import { queryKeys } from './queryKeys';
import { useGetUserFromAccessToken } from '../common/auth';
import { User } from '../types/User';
import { queryClient } from '../queryClient';
import { useGetActualSpaceId, useWorkspaceStore } from './workspace';
import Workspace from '../types/Workspace';
import { flushSync } from 'react-dom';
import { Epic } from '../types/Epic';
import { addToSpaceTasksOrder } from './helpers/tasksOrderHelper';
import { insertIntoList } from './helpers/listCache';
import { TASKS_MUTATION_KEY, TASK_MUTATION_KEY } from './helpers/inFlightTasks';
import { deleteTasks, updateTasks } from '../api/workspace';
import { UpdateSpaceTasksModel } from '../api/types/workspace.types';
import { TaskComment } from '../types/Comment';
import { TaskTableDataType } from '../components/TaskTable/types/TaskTableTypes';
import { analytics } from '../analytics/analytics';
import { AnalyticsEvent } from '../analytics/analyticsEvent';
import { GroupItem } from '../components/Backlog/TaskGroupSection/TaskGroupSection';
import { Column } from '../types/WorkspaceViewColumn';
import { PriorityOptions } from '../components/TaskTable/helpers/helper';
import { TaskStatus } from '../types/TaskStatus';
import { usePlanningSession } from './planning';
import { getCurrentWorkspaceActiveSprint } from './sprint';
import { getLocalISODateString } from '../common/dateUtils';
import { TaskLink } from '../types/TaskLink';
import { TaskDependencies, TaskDependencyDirection, TaskDependencyTask } from '../types/TaskDependency';
import { TaskAttachment } from '../types/TaskAttachment';
import { QUERY_CONFIG } from '../constants/queryConfig';
import { TaskType } from '../types/TaskType';

export const useGetTaskComments = (taskId: number) => {
  return useQuery<TaskComment[], Error>({
    queryKey: ['taskComments', taskId],
    queryFn: async () => {
      const res = await getTaskComments(taskId);
      return res.data;
    },
    enabled: !!taskId,
    staleTime: QUERY_CONFIG.STALE_TIME,
  });
};

export const useGetTaskActivity = (taskId: number) => {
  return useQuery<TaskActivityResponse, Error>({
    queryKey: queryKeys.taskActivity(taskId),
    queryFn: async () => {
      const res = await getTaskActivity(taskId);
      return res.data;
    },
    enabled: !!taskId,
    staleTime: QUERY_CONFIG.STALE_TIME,
  });
};

type ToggleReactionArgs = { commentId: number; emoji: string; add: boolean };

// Toggle an emoji reaction on a comment with an optimistic cache update so the
// pill flips instantly; the taskCommentsChanged socket event reconciles peers.
// Returns a `toggle(commentId, emoji, hasReacted)` that guards against rapid
// double-clicks: while a given (comment, emoji) toggle is in flight, repeat
// clicks are ignored so we never fire contradictory add/remove requests.
export const useToggleCommentReaction = (taskId: number) => {
  const currentUser = useGetUserFromAccessToken().data?.user;
  const inFlight = useRef<Set<string>>(new Set());

  const mutation = useMutation({
    mutationFn: ({ commentId, emoji, add }: ToggleReactionArgs) =>
      add ? addCommentReaction(taskId, commentId, emoji) : removeCommentReaction(taskId, commentId, emoji),
    onMutate: async ({ commentId, emoji, add }: ToggleReactionArgs) => {
      const queryKey = ['taskComments', taskId];
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<TaskComment[]>(queryKey);

      if (previous && currentUser) {
        const userId = currentUser.id;
        queryClient.setQueryData<TaskComment[]>(
          queryKey,
          previous.map((comment) => {
            if (comment.id !== commentId) return comment;
            const existingReactions = comment.Reactions ?? [];
            const alreadyReacted = existingReactions.some((r) => r.userId === userId && r.emoji === emoji);
            if (!add) {
              return {
                ...comment,
                Reactions: existingReactions.filter((r) => !(r.userId === userId && r.emoji === emoji)),
              };
            }
            if (alreadyReacted) return comment; // idempotent: don't duplicate
            const optimistic: CommentReaction = {
              id: -Date.now(),
              commentId,
              userId,
              emoji,
              createdAt: new Date(),
              User: currentUser as unknown as User,
            };
            return { ...comment, Reactions: [...existingReactions, optimistic] };
          })
        );
      }

      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['taskComments', taskId], context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['taskComments', taskId] });
    },
  });

  return (commentId: number, emoji: string, hasReacted: boolean) => {
    const key = `${commentId}:${emoji}`;
    if (inFlight.current.has(key)) return;
    inFlight.current.add(key);
    mutation.mutate({ commentId, emoji, add: !hasReacted }, { onSettled: () => inFlight.current.delete(key) });
  };
};

export const useGetTaskLinks = (taskId: number) => {
  return useQuery<TaskLink[], Error>({
    queryKey: ['taskLinks', taskId],
    queryFn: async () => {
      const res = await getTaskLinks(taskId);
      return res.data.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    },
    enabled: !!taskId,
    staleTime: QUERY_CONFIG.STALE_TIME,
  });
};

export const useGetTaskDependencies = (taskId?: number) => {
  return useQuery<TaskDependencies, Error>({
    queryKey: queryKeys.taskDependencies(taskId!),
    queryFn: async () => (await getTaskDependencies(taskId!)).data,
    enabled: !!taskId,
    staleTime: QUERY_CONFIG.STALE_TIME,
  });
};

/** The subset of a task the Blocking / Blocked By cells and chips render. */
const toDependencyTask = (task: Task): TaskDependencyTask => ({
  id: task.id,
  taskId: task.taskId,
  title: task.title,
  type: task.type,
  statusId: task.statusId,
});

/**
 * Patch the two tasks a dependency edge touches, in place, instead of invalidating the whole
 * space list.
 *
 * Invalidating meant refetching every task in the space, and the refetch hands every row a
 * brand-new object — which is the first thing VirtualizedTaskRow's memo comparator checks,
 * so every visible row re-rendered. At ~18ms a row that was ~450ms of blocked main thread per
 * click, and it scaled with rows on screen rather than with the one edge that changed. Only
 * two tasks can change here, so only those two get a new identity and re-render. (#2803)
 *
 * An edge is stored from both ends: A blocking B is A.BlockingEdges + B.BlockedByEdges, and
 * both sides share the edge id — so a removal can just drop that id wherever it appears.
 */
const patchDependencyEdgeInList = (
  workspaceId: number,
  args:
    | { kind: 'add'; edgeId: number; taskId: number; relatedTaskId: number; type: TaskDependencyDirection }
    | { kind: 'remove'; edgeId: number }
) => {
  queryClient.setQueryData<TaskTableDataType[]>(queryKeys.tasks(workspaceId), (list) => {
    if (!list) return list;

    if (args.kind === 'remove') {
      return list.map((task) => {
        const blocking = task.BlockingEdges ?? [];
        const blockedBy = task.BlockedByEdges ?? [];
        if (!blocking.some((e) => e.id === args.edgeId) && !blockedBy.some((e) => e.id === args.edgeId)) {
          return task;
        }
        return {
          ...task,
          BlockingEdges: blocking.filter((e) => e.id !== args.edgeId),
          BlockedByEdges: blockedBy.filter((e) => e.id !== args.edgeId),
        };
      });
    }

    const owner = list.find((t) => t.id === args.taskId);
    const related = list.find((t) => t.id === args.relatedTaskId);
    // A cross-space link has no row for the other end here; leave the list alone and let the
    // caller fall back to a refetch rather than write a half-formed edge.
    if (!owner || !related) return list;

    // `type` is stated from the owner's side, so the related task always gets the mirror.
    const ownerBlocks = args.type === 'blocking';
    return list.map((task) => {
      if (task.id === args.taskId) {
        return ownerBlocks
          ? {
              ...task,
              BlockingEdges: [...(task.BlockingEdges ?? []), { id: args.edgeId, Blocked: toDependencyTask(related) }],
            }
          : {
              ...task,
              BlockedByEdges: [
                ...(task.BlockedByEdges ?? []),
                { id: args.edgeId, Blocking: toDependencyTask(related) },
              ],
            };
      }
      if (task.id === args.relatedTaskId) {
        return ownerBlocks
          ? {
              ...task,
              BlockedByEdges: [...(task.BlockedByEdges ?? []), { id: args.edgeId, Blocking: toDependencyTask(owner) }],
            }
          : {
              ...task,
              BlockingEdges: [...(task.BlockingEdges ?? []), { id: args.edgeId, Blocked: toDependencyTask(owner) }],
            };
      }
      return task;
    });
  });
};

/**
 * Copy the edges `patchDependencyEdgeInList` just computed onto the two detail records.
 *
 * The invalidate below is a backstop, not a fix on its own: it only MARKS the record stale and
 * STARTS a refetch, while `saveTask` reads `task` — useTaskDetails' local state, synced from
 * `taskDetailQuery.data`. A save issued before that refetch lands still spreads the stale edges
 * array into `mergeTaskListRow`, whose `incoming.BlockingEdges ?? existing` takes it, and the
 * edge is wiped off the list row for the 24h life of ['tasks', ws]. The 1s description debounce
 * hides that window; title / status / assignee save immediately and do not.
 *
 * No merge logic is duplicated here: on this path `patchDependencyEdgeInList` has ALREADY built
 * the exact edge shape the server returns (`{ id, Blocked | Blocking: toDependencyTask(...) }`),
 * so this only reads back what it wrote. It is deliberately limited to the `canPatchLocally`
 * path — where the other end is missing there is nothing correct to copy, and the invalidate is
 * then the only honest answer. (#3010)
 */
const mirrorDependencyEdgesToDetail = (workspaceId: number, taskIds: number[]) => {
  const list = queryClient.getQueryData<TaskTableDataType[]>(queryKeys.tasks(workspaceId));
  for (const id of taskIds) {
    const patched = list?.find((t) => t.id === id);
    if (!patched) continue;
    writeTaskDetailCache({
      id: patched.id,
      BlockingEdges: patched.BlockingEdges,
      BlockedByEdges: patched.BlockedByEdges,
    });
  }
};

/** True when both ends of the edge are rows we could patch; otherwise we must refetch. */
const canPatchLocally = (workspaceId: number, taskId: number, relatedTaskId: number) => {
  const list = queryClient.getQueryData<TaskTableDataType[]>(queryKeys.tasks(workspaceId));
  return !!list?.some((t) => t.id === taskId) && !!list.some((t) => t.id === relatedTaskId);
};

// `workspaceId` MUST be numeric — the route `spaceId` is a string and would build
// a non-matching ['tasks', '<id>'] key, silently skipping the table-list refresh.
export const useAddTaskDependency = (taskId: number, workspaceId: number) => {
  return useMutation({
    mutationFn: ({ relatedTaskId, type }: { relatedTaskId: number; type: TaskDependencyDirection }) =>
      addTaskDependency(taskId, relatedTaskId, type),
    onSuccess: (res, { relatedTaskId, type }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.taskDependencies(taskId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.taskDependencies(relatedTaskId) });
      const edgeId = res.data?.id;
      if (edgeId != null && canPatchLocally(workspaceId, taskId, relatedTaskId)) {
        patchDependencyEdgeInList(workspaceId, { kind: 'add', edgeId, taskId, relatedTaskId, type });
        mirrorDependencyEdgesToDetail(workspaceId, [taskId, relatedTaskId]);
      } else {
        void queryClient.invalidateQueries({ queryKey: queryKeys.tasks(workspaceId) });
      }
      // This mutation is wired inside Task View, so it fires while the detail record is
      // ON SCREEN — `refetchOnMount: 'always'` can't cover it. The patch above updates the
      // LIST row's edges only; leave the detail record stale and the next saveTask spreads
      // its outdated edges back over the list row through mergeTaskListRow, dropping the
      // edge from the Backlog's Blocking / Blocked By column for the rest of the session
      // (['tasks', ws] is staleTime 24h).
      //
      // The mirror above makes the local-patch path deterministic; this invalidate is the
      // backstop for the path it cannot cover (a cross-space edge, where the other end has no
      // row to read back) and for anything the patch did not compute. On its own it is not
      // enough — it only starts a refetch, and a save that beats it still reads the stale
      // record. (#3010)
      invalidateTaskDetailCache([taskId, relatedTaskId]);
    },
  });
};

export const useRemoveTaskDependency = (taskId: number, workspaceId: number) => {
  return useMutation({
    mutationFn: ({ dependencyId }: { dependencyId: number; relatedTaskId: number }) =>
      deleteTaskDependency(taskId, dependencyId),
    onSuccess: (_res, { relatedTaskId, dependencyId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.taskDependencies(taskId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.taskDependencies(relatedTaskId) });
      if (canPatchLocally(workspaceId, taskId, relatedTaskId)) {
        patchDependencyEdgeInList(workspaceId, { kind: 'remove', edgeId: dependencyId });
        mirrorDependencyEdgesToDetail(workspaceId, [taskId, relatedTaskId]);
      } else {
        void queryClient.invalidateQueries({ queryKey: queryKeys.tasks(workspaceId) });
      }
      // Mirror of the add case: without this the removed edge is still on the detail record,
      // and the next saveTask writes it back onto the list row. Same split of duties — the
      // mirror above covers the patched path synchronously, this covers the rest. (#3010)
      invalidateTaskDetailCache([taskId, relatedTaskId]);
    },
  });
};

export const useGetTaskAttachments = (taskId: number) => {
  return useQuery<TaskAttachment[], Error>({
    queryKey: ['taskAttachments', taskId],
    queryFn: async () => {
      const res = await getTaskAttachments(taskId);
      return res.data.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    },
    enabled: !!taskId,
    staleTime: QUERY_CONFIG.STALE_TIME,
  });
};

export const useGetTaskSubtasks = (taskId: number) => {
  return useQuery<Task[], Error>({
    queryKey: ['taskSubtasks', taskId],
    queryFn: async () => {
      const res = await getTaskSubtasks(taskId);
      return res.data;
    },
    enabled: !!taskId,
    staleTime: QUERY_CONFIG.STALE_TIME,
  });
};

export const useGetTasks = (workspaceId: number) => {
  return useQuery<TaskTableDataType[], Error>({
    queryKey: ['tasks', workspaceId],
    queryFn: async () => {
      const res = await getTasksForGivenSpace(workspaceId);
      return res.data?.map((t) => {
        return {
          ...t,
          key: t.id,
        };
      });
    },
    enabled: !!workspaceId,
    staleTime: QUERY_CONFIG.STALE_TIME,
  });
};

export const useGetArchivedTasks = (workspaceId: number, enabled = true) => {
  return useQuery<TaskTableDataType[], Error>({
    queryKey: ['tasks', workspaceId, 'archive'],
    queryFn: async () => {
      const res = await getTasksForGivenSpace(workspaceId, 'archive');
      return res.data?.map((t) => ({ ...t, key: t.id }));
    },
    enabled: !!workspaceId && enabled,
    staleTime: QUERY_CONFIG.STALE_TIME,
  });
};

/**
 * A single task, fetched directly instead of scanned out of the space list.
 *
 * `placeholderData` reuses the row already in the list cache so a click from a module
 * paints with no spinner; on a cold load there is no row, so the first paint waits on
 * one small request rather than on the whole space download. Placeholder data is never
 * written to the cache, so a list row can't be mistaken for a complete record.
 *
 * `refetchOnMount: false` is the opt-out for a *secondary* observer of the same key — the
 * prefetch in Space.tsx, which exists only to start the request early and is not the thing
 * rendering the record. Task View's own observer keeps the default `'always'`, so the
 * revalidate-on-open guarantee is unchanged. (#3010)
 */
export const useTaskByNumber = (
  spacePublicId: string | undefined,
  taskNumber: number | undefined,
  options?: { refetchOnMount?: 'always' | false }
) => {
  // Stable reference: query-core only takes its memo branch when this identity holds
  // (queryObserver.js:282). Do NOT hoist to module scope — it would then return the
  // previous task's placeholder after a prev/next navigation.
  const placeholderData = useCallback(() => {
    // Same source useGetActualSpaceId reads; this hook only has the public id.
    const numericSpaceId = Number(useWorkspaceStore.getState().currentWorkspace);
    if (!numericSpaceId || !taskNumber || !spacePublicId) return undefined;
    // The persisted store lags a cross-space navigation (#2796), and task numbers are
    // per-space — without this check the placeholder paints a same-numbered task from
    // the previously-open space. Space.tsx:139 seeds ['workspace', <numericId>].
    const ws = queryClient.getQueryData<Workspace>(queryKeys.workspace(numericSpaceId));
    if (ws?.publicId !== spacePublicId) return undefined;
    const pools = [
      queryClient.getQueryData<Task[]>(queryKeys.tasks(numericSpaceId)),
      queryClient.getQueryData<Task[]>(queryKeys.archivedTasks(numericSpaceId)),
      queryClient.getQueryData<Task[]>(queryKeys.epics(numericSpaceId)),
    ];
    for (const pool of pools) {
      const hit = pool?.find((t) => t.taskId === taskNumber);
      if (hit) return hit;
    }
    return undefined;
  }, [spacePublicId, taskNumber]);

  return useQuery<Task, Error>({
    queryKey: queryKeys.taskDetail(spacePublicId ?? '', taskNumber ?? 0),
    queryFn: async () => (await getTaskByNumber(spacePublicId!, taskNumber!)).data,
    enabled: !!spacePublicId && !!taskNumber,
    // NOT QUERY_CONFIG.STALE_TIME (24h). placeholderData already gives the instant
    // paint, so the detail record can revalidate on every mount — that is what keeps
    // it correct against the ~20 list-module write paths that never patch this cache.
    staleTime: 0,
    refetchOnMount: options?.refetchOnMount ?? 'always',
    placeholderData,
  });
};

/**
 * The one writer for the task-detail cache. Task View's own saves, the AI modals and
 * the socket handlers all funnel through here — otherwise the detail record Task View
 * renders goes stale the moment anything else edits the task, which the list-only
 * cache never had to worry about.
 *
 * Merges rather than replaces: socket payloads omit dependency edges, and a bare
 * replace would blank them. Because it merges, it is NOT usable as a rollback — see
 * saveTask, which restores a snapshot instead.
 */
export const writeTaskDetailCache = (task: Pick<Task, 'id'> & Partial<Task>) => {
  queryClient.setQueriesData<Task>({ queryKey: queryKeys.taskDetailRoot() }, (old) =>
    old && old.id === task.id ? { ...old, ...task } : old
  );
};

/**
 * Merge an optimistic task onto the list row it replaces, keeping the fields the list
 * carries but a detail record does not.
 *
 * `useGetTasks` stamps `key: t.id` on every row; `Task` has no `key`, so a task built
 * from the detail record would write `key: undefined` and silently break planning row
 * selection (PlanningSession, SprintPlanningTable) and column auto-sizing
 * (textMeasurement). Labels and dependency edges get the same treatment: a partial
 * optimistic task may omit them, and a wholesale replace would blank the Blocking /
 * Blocked By columns. (#3010)
 */
export const mergeTaskListRow = <T extends Task>(existing: T, incoming: T): T =>
  ({
    ...incoming,
    key:
      (incoming as T & Partial<TaskTableDataType>).key ??
      (existing as T & Partial<TaskTableDataType>).key ??
      incoming.id,
    TaskLabels: incoming.TaskLabels ?? existing.TaskLabels,
    BlockingEdges: incoming.BlockingEdges ?? existing.BlockingEdges,
    BlockedByEdges: incoming.BlockedByEdges ?? existing.BlockedByEdges,
  }) as T;

/**
 * Undo an optimistic detail write by restoring the snapshot taken before it.
 *
 * This is the ONLY correct rollback: `writeTaskDetailCache` merges, so it can neither
 * undo a field the optimistic write *added* nor no-op its way back to "no record".
 * `setQueryData` ignores an updater returning undefined (query-core queryClient.js:94-97),
 * so an absent snapshot has to be evicted through `removeQueries`. [M9][B1]
 *
 * Asymmetry, deliberate: `writeTaskDetailCache` patches EVERY `['taskDetail']` entry
 * whose id matches, while this restores only the one key the caller snapshotted. The
 * same task can sit under two keys, because useTaskNavigation.ts:158 navigates by
 * numeric workspace id while normal navigation uses the public id. The unrestored twin
 * self-corrects on its next mount — that query is `staleTime: 0` +
 * `refetchOnMount: 'always'` — and only the snapshotted key is the one on screen.
 */
export const rollbackTaskDetailCache = (key: QueryKey, snapshot: Task | undefined) => {
  if (snapshot) queryClient.setQueryData(key, snapshot);
  else queryClient.removeQueries({ queryKey: key });
};

/** Drop cached detail records for deleted tasks. */
export const removeTaskDetailCache = (taskIds: number[]) => {
  const ids = new Set(taskIds);
  // setQueryData ignores an updater that returns undefined (query-core
  // queryClient.js:94-97), so eviction has to go through removeQueries.
  queryClient.removeQueries({
    queryKey: queryKeys.taskDetailRoot(),
    predicate: (q) => ids.has((q.state.data as Task | undefined)?.id as number),
  });
};

/**
 * Invalidate cached detail records for tasks touched by a *bulk* update whose fields
 * we can't safely hand-merge from the socket payload alone.
 *
 * A bulk edit (sprint move, stage/archive change, label add/remove) computes its
 * per-task result inside the list-cache patch (`mapListCache` in useSpaceSocket.tsx) —
 * preserved/merged `TaskLabels`, `stage`, `sprintId`, `planningSessionId`,
 * `archivedAt` — and that computed result never leaves the list-patch closure.
 * Reproducing it here would mean carrying a second copy of that merge logic that can
 * silently drift from the list one (#3010 review finding). This query has
 * `staleTime: 0` and `refetchOnMount: 'always'`, and invalidateQueries defaults to
 * `refetchType: 'active'`, so an open Task View (a live observer) refetches
 * immediately and ends up correct by construction — no merge duplication needed.
 */
export const invalidateTaskDetailCache = (taskIds: number[]) => {
  const ids = new Set(taskIds);
  void queryClient.invalidateQueries({
    queryKey: queryKeys.taskDetailRoot(),
    predicate: (q) => ids.has((q.state.data as Task | undefined)?.id as number),
  });
};

export const useCreateTask = () => {
  return useMutation({
    mutationFn: async ({
      workspaceId,
      epicId,
      title,
      planningSessionId,
      statusId,
      priority,
      sprintId,
      estimate,
      storyPoints,
      startDate,
      deadline,
      type,
      parentTaskId,
      assignee,
      description,
      stage,
    }: {
      workspaceId: number;
      epicId?: number;
      title?: string;
      planningSessionId?: number;
      statusId?: number;
      priority?: string;
      sprintId?: number;
      estimate?: number;
      storyPoints?: number;
      deadline?: string;
      type?: string;
      parentTaskId?: number;
      assignee?: number;
      startDate?: string;
      description?: string;
      stage?: TaskStage;
    }) => {
      const newTask = await createPlaceholderTask(
        workspaceId,
        epicId,
        title,
        planningSessionId,
        statusId,
        priority,
        sprintId,
        estimate,
        storyPoints,
        startDate,
        deadline,
        type,
        parentTaskId,
        assignee,
        description,
        stage
      );
      // analytics
      analytics.track(AnalyticsEvent.taskCreated, {
        taskId: newTask.data.id,
        taskType: type === TaskType.epic ? 'epic' : parentTaskId ? 'subitem' : 'item',
        spaceId: workspaceId,
      });
      return newTask.data;
    },
    onError: (err) => {
      console.error('Failed to create task:', err);
    },
  });
};

/**
 * Batch operations MUST run through useMutation: the reconnect backfill in
 * useSpaceSocket defers on queryClient.isMutating(), and bare API calls are
 * invisible to that counter (audit F6).
 */
/** Shares TASK_MUTATION_KEY with useUpdateTaskMutation: both are single-task writes. */
export const useUpdateTaskLabelsMutation = () =>
  useMutation({
    mutationKey: TASK_MUTATION_KEY,
    mutationFn: ({ taskId, labelIds }: { taskId: number; labelIds: number[] }) =>
      updateTaskLabels(taskId, { labelIds }),
  });

export const useUpdateTasksMutation = () =>
  useMutation({
    mutationKey: TASKS_MUTATION_KEY,
    mutationFn: ({
      spaceId,
      taskIds,
      changes,
    }: {
      spaceId: number;
      taskIds: number[];
      changes: UpdateSpaceTasksModel;
    }) => updateTasks(spaceId, taskIds, changes),
  });

export const useDeleteTasksMutation = () =>
  useMutation({
    mutationKey: TASKS_MUTATION_KEY,
    mutationFn: ({ spaceId, taskIds }: { spaceId: number; taskIds: number[] }) => deleteTasks(spaceId, taskIds),
  });

export const useCreateTaskInWorkspace = () => {
  const createTaskMutation = useCreateTask();

  const actualSpaceId = useGetActualSpaceId();
  const activeSprintId = getCurrentWorkspaceActiveSprint().data?.sprint.id;
  const activePlanningSessionId = usePlanningSession().data?.session.id;

  const createTaskInGroup = (addToTop: boolean, groupBy: string, groupItem: GroupItem, planningSessionId?: number) => {
    switch (groupBy) {
      case Column.taskType:
        console.log(groupItem);
        return createTask(
          addToTop,
          null,
          planningSessionId,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          groupItem as string
        );
      case Column.epic:
        return createTask(addToTop, groupItem as Epic, planningSessionId);
      case Column.assignee:
        return createTask(addToTop, null, planningSessionId);
      case Column.status:
        return createTask(addToTop, null, planningSessionId, (groupItem as TaskStatus)?.id);
      case Column.estimate:
        return createTask(
          addToTop,
          null,
          planningSessionId,
          undefined,
          undefined,
          undefined,
          Number(groupItem as string)
        );
      case Column.priority:
        return createTask(addToTop, null, planningSessionId, undefined, groupItem as PriorityOptions);
      case Column.deadline:
        if (groupItem) {
          const [month, year] = (groupItem as string).split(', ');
          const parsedDeadline = new Date(`${month} 1, ${year}`);
          return createTask(
            addToTop,
            null,
            planningSessionId,
            undefined,
            undefined,
            undefined,
            undefined,
            getLocalISODateString(parsedDeadline)
          );
        } else {
          return createTask(addToTop, null, planningSessionId);
        }
      case Column.sprintStatus:
        const sprintStatus = groupItem as 'planning' | 'sprint' | 'kanban' | 'none';
        return createTask(
          addToTop,
          null,
          sprintStatus === 'planning' ? activePlanningSessionId : planningSessionId,
          undefined,
          undefined,
          sprintStatus === 'sprint' || sprintStatus === 'kanban' ? activeSprintId : undefined
        );
    }
  };

  const createTask = async (
    addToTop = false,
    epic: Epic | null = null,
    planningSessionId?: number,
    statusId?: number,
    priority?: string,
    sprintId?: number,
    estimate?: number,
    deadline?: string,
    taskType?: string
  ) => {
    createTaskMutation.mutate(
      {
        workspaceId: parseInt(actualSpaceId as string),
        epicId: epic?.id,
        planningSessionId: planningSessionId,
        statusId: statusId,
        priority: priority,
        sprintId: sprintId,
        estimate: estimate,
        deadline: deadline,
        type: taskType,
      },
      {
        // Order persistence is fire-and-forget so creation feels instant at
        // 2k+ tasks. If ordering ever lands wrong on refresh after a failed
        // PATCH, revert by awaiting addToSpaceTasksOrder before the flushSync
        // here and in createTaskInKanban below, and move updateTasksOrderLocally
        // back to after the await in tasksOrderHelper.ts:addToSpaceTasksOrder.
        onSuccess: (data) => {
          flushSync(() => {
            queryClient.setQueryData(['tasks', parseInt(actualSpaceId as string)], (oldData: Task[] | undefined) =>
              insertIntoList(oldData, { ...data, key: data.id }, addToTop)
            );
          });

          setTimeout(() => {
            flushSync(() => {
              document.querySelector(`[task-title-id='${Number(data.id)}']`)?.click();
            });
            const taskTitleInput = document.querySelector(`[task-title-input-id='${Number(data.id)}']`);
            taskTitleInput?.focus();
            taskTitleInput?.select();
          }, 700);

          addToSpaceTasksOrder(Number(actualSpaceId), data.id, addToTop).catch((err) => {
            console.error('Failed to persist task order on create:', err);
          });
        },
      }
    );
  };

  const createTaskInKanban = async (statusId: number, sprintId?: number, addToTop = false) => {
    return new Promise((resolve, reject) => {
      createTaskMutation.mutate(
        {
          workspaceId: parseInt(actualSpaceId as string),
          statusId: statusId,
          sprintId: sprintId,
        },
        {
          onSuccess: (data) => {
            flushSync(() => {
              queryClient.setQueryData(['tasks', parseInt(actualSpaceId as string)], (oldData: Task[] | undefined) =>
                insertIntoList(oldData, { ...data, key: data.id }, addToTop)
              );
            });

            addToSpaceTasksOrder(Number(actualSpaceId), data.id, addToTop).catch((err) => {
              console.error('Failed to persist task order on create:', err);
            });

            resolve(data);
          },
          onError: (error) => {
            reject(error);
          },
        }
      );
    });
  };

  return {
    createTaskInGroup,
    createTask,
    createTaskInKanban,
    createTaskMutation,
  };
};

export const useUpdateTaskMutation = () => {
  return useMutation({
    // Keyed so isTaskMutating() can tell a socket echo of our own write from a
    // peer's change (serverState/helpers/inFlightTasks.ts).
    mutationKey: TASK_MUTATION_KEY,
    mutationFn: async ({ taskId, task }: { taskId: number; task: Partial<Task> }) => {
      const newTask = await updateTask(taskId, task);
      return newTask.data;
    },
  });
};
